import type { RouteRecordRaw } from "vue-router"
import type { OrganizationScopeType } from "@/types/organization-scope"
import { markRaw } from "vue"
import { MEMBER_RISK_AVAILABLE } from "@/features/memberRiskAvailability"
import { injectRouteList } from "@/hooks/useRouter"
import LevelTwoRouter from "@/layout/components/NavigationBar/LevelTwoRouter.vue"
import { MEMBERSHIP_ACTION_PERMISSIONS, MEMBERSHIP_SETTING_ACTION_PERMISSIONS } from "@/utils/roleFeatureAccess"
import { MembershipChargeRuleRoute, MembershipCreditRoute, MembershipDashboardRoute, MembershipLevelRoute, MembershipManageRoute } from "../RouteNameEnum"
import notAccessImage from '/empty/membership_empty.jpg?url'

const Layout = () => import("@/layout/index.vue")
const MemberManage = () => import("@/views/page/membership/MemberManage.vue")
const ConsumptionRecord = () => import("@/views/page/membership/ConsumptionRecord.vue")
const TopUpRecord = () => import("@/views/page/membership/TopUpRecord.vue")
const MembershipOption = () => import("@/views/page/membership/MembershipOption.vue")
const CreditShop = () => import("@/views/page/creditshop/CreditShop.vue")
const AddOrEditChargeRule = () => import("@/views/page/setting/components/MembershipManage/AddOrEditChargeRule.vue")
const ChargeRule = () => import("@/views/page/setting/components/MembershipManage/ChargeRule.vue")
const MemberConsume = () => import("@/views/page/dashboard/components/MemberStatement/MemberConsume.vue")
const MemberTopUp = () => import("@/views/page/dashboard/components/MemberStatement/MemberTopUp.vue")
const MembershipLevelManage = () => import("@/views/page/setting/components/MembershipManage/MembershipLevelManage.vue")
// const MemberDetail = () => import("@/views/page/membership/MemberIndex.vue")
const MemberDetail = () => import("@/views/page/membership/components/MemberManageDetail.vue")
const AddOrEditMembershipLevel = () =>
  import("@/views/page/setting/components/MembershipManage/AddOrEditMembershipLevel.vue")

const MEMBERSHIP_MEMBER_MANAGE_GROUP_PERMISSIONS = [
  "membership_info",
  "membership_info_get_list",
  "membership_info_get",
  "membership_info_option",
]

const MEMBERSHIP_BUSINESS_GROUP_PERMISSIONS = [
  "membership_info_consumption",
  "membership_balance_charge_records",
]

const MEMBERSHIP_CREDIT_GROUP_PERMISSIONS = [
  "membership_credit_show",
  "membership_credit_use",
]

const MEMBERSHIP_RISK_WARNING_GROUP_PERMISSIONS = MEMBERSHIP_ACTION_PERMISSIONS.view
const STORE_SCOPE_TYPES: OrganizationScopeType[] = ["shop", "chain-store"]

const MembershipManageRouter: RouteRecordRaw[] = [
  {
    path: "/membership/member-manage/member-stat",
    redirect: "/membership/member-manage/member-stat/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "会员管理",
      permissions: ["membership_info"],
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/membership/member-manage/member-stat/index",
        component: MemberManage,
        name: MembershipManageRoute.MembershipManage,
        meta: {
          title: "会员管理",
          permissions: ["membership_info_get_list"],
          notAccessImage,
          hidden: true,
        },
      },
      {
        path: "/membership/member-manage/member-stat/detail/:merchantCustomerUserId",
        component: MemberDetail,
        name: MembershipManageRoute.MemberDetail,
        meta: {
          title: "会员详情",
          permissions: ["membership_info_get"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
]

const MembershipOperationRecordRouter: RouteRecordRaw[] = [
  {
    path: "/membership/member-manage/operation-record",
    redirect: "/membership/member-manage/operation-record/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "会员操作记录",
      permissions: ["membership_info_option"],
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/membership/member-manage/operation-record/index",
        component: MembershipOption,
        name: MembershipManageRoute.OperationRecord,
        meta: {
          title: "会员操作记录",
          permissions: ["membership_info_option"],
          hidden: true,
          notAccessImage,
        },
      },
    ],
  },
]

export const MembershipManageChildrenRouter: RouteRecordRaw[] = [
  ...MembershipManageRouter,
  ...MembershipOperationRecordRouter,
]

const MembershipBusinessRouter: RouteRecordRaw[] = [
  {
    path: "/membership/business/consumption-record",
    redirect: "/membership/business/consumption-record/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "会员消费记录",
      permissions: ["membership_info_consumption"],
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/membership/business/consumption-record/index",
        component: ConsumptionRecord,
        name: MembershipManageRoute.ConsumptionRecord,
        meta: {
          title: "会员消费记录",
          permissions: ["membership_info_consumption"],
          notAccessImage,
          hidden: true,
        },
      },
    ],
  },
  {
    path: "/membership/business/top-up-record",
    redirect: "/membership/business/top-up-record/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "会员充值记录",
      permissions: ["membership_balance_charge_records"],
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/membership/business/top-up-record/index",
        component: TopUpRecord,
        name: MembershipManageRoute.TopUpRecord,
        meta: {
          title: "会员充值记录",
          permissions: ["membership_balance_charge_records"],
          notAccessImage,
          hidden: true,
        },
      },
    ],
  },
]

export const MembershipBusinessChildrenRouter: RouteRecordRaw[] = [
  ...MembershipBusinessRouter,
]

const MembershipCreditRouter: RouteRecordRaw[] = [
  {
    path: "/membership/credit/credit-shop",
    redirect: "/membership/credit/credit-shop/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "积分商城",
      permissions: ["membership_credit_show"],
      keepAlive: true,
      hidden: true,
      hideInLevelTwoTabs: true,
      notAccessImage,
    },
    children: [
      {
        path: "/membership/credit/credit-shop/index",
        component: CreditShop,
        name: MembershipCreditRoute.CreditShop,
        meta: {
          title: "积分商城",
          permissions: ["membership_credit_show"],
          notAccessImage,
          hidden: true,
        },
      },
    ],
  },
  {
    path: "/membership/credit/credit-get-records/:pathMatch(.*)*",
    redirect: "/membership/member-manage/member-stat/index",
    meta: {
      title: "积分记录",
      permissions: ["membership_credit_get_records"],
      hidden: true,
      hideInLevelTwoTabs: true,
      notAccessImage,
    },
  },
  {
    path: "/membership/credit/credit-consumption-records/:pathMatch(.*)*",
    redirect: to => ({
      path: "/membership/credit/credit-shop/index",
      query: { ...to.query, tab: "records" },
    }),
    meta: {
      title: "兑换记录",
      permissions: ["membership_credit_use"],
      hidden: true,
      hideInLevelTwoTabs: true,
      notAccessImage,
    },
  },
]

export const MembershipCreditChildrenRouter: RouteRecordRaw[] = [
  ...MembershipCreditRouter,
]

const MemberChargeRuleRouter: RouteRecordRaw[] = [
  {
    path: "/membership/marketing/charge-rule",
    redirect: "/membership/marketing/charge-rule/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "充值规则",
      permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.viewChargeRules,
      keepAlive: true,
      hidden: true,
      notAccessImage,
    },
    children: [
      {
        path: "/membership/marketing/charge-rule/index",
        component: ChargeRule,
        name: MembershipChargeRuleRoute.ChargeRule,
        meta: {
          title: "充值规则",
          permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.viewChargeRules,
          notAccessImage,
          hidden: true,
        },
      },
      {
        path: "/membership/marketing/charge-rule/create",
        name: MembershipChargeRuleRoute.AddChargeRule,
        component: AddOrEditChargeRule,
        meta: {
          title: "添加充值规则",
          hidden: true,
          permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.manageChargeRules,
        },
      },
      {
        path: "/membership/marketing/charge-rule/edit",
        name: MembershipChargeRuleRoute.EditChargeRule,
        component: AddOrEditChargeRule,
        meta: {
          title: "编辑充值规则",
          hidden: true,
          permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.manageChargeRules,
        },
      },
    ],
  },
]

export const MemberChargeRuleChildrenRouter: RouteRecordRaw[] = [
  ...MemberChargeRuleRouter,
]

const MembershipDashboardRouter: RouteRecordRaw[] = [
  {
    path: "/membership/dashboard/member-consume",
    redirect: "/membership/dashboard/member-consume/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "会员消费报表",
      notAccessImage,
      hidden: true,
      permissions: ["dashboard_member"],
    },
    children: [
      {
        path: "/membership/dashboard/member-consume/index",
        component: MemberConsume,
        name: MembershipDashboardRoute.MemberConsume,
        meta: {
          title: "会员消费统计",
          hidden: true,
          permissions: ["dashboard_member"],
        },
      },
    ],
  },
  {
    path: "/membership/dashboard/member-top-up",
    redirect: "/membership/dashboard/member-top-up/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "会员充值报表",
      notAccessImage,
      hidden: true,
      permissions: ["dashboard_member"],
    },
    children: [
      {
        path: "/membership/dashboard/member-top-up/index",
        component: MemberTopUp,
        name: MembershipDashboardRoute.MemberTopUp,
        meta: {
          title: "会员充值统计",
          hidden: true,
          permissions: ["dashboard_member"],
        },
      },
    ],
  },
]

const membershipLevelRouter: RouteRecordRaw[] = [
  {
    path: "/membership/member-level/setting",
    redirect: "/membership/member-level/setting/index",
    component: markRaw(LevelTwoRouter),
    meta: {
      title: "会员卡等级",
      hidden: true,
      permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.viewLevels,
    },
    children: [
      {
        path: "/membership/member-level/setting/index",
        component: MembershipLevelManage,
        name: MembershipLevelRoute.MembershipLevelSetting,
        meta: {
          title: "会员卡等级",
          hidden: true,
          permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.viewLevels,
        },
      },
      {
        path: "/membership/member-level/setting/create",
        name: MembershipLevelRoute.AddMembershipLevel,
        component: AddOrEditMembershipLevel,
        meta: {
          title: "添加会员卡等级",
          hidden: true,
          permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.manageLevels,
        },
      },
      {
        path: "/membership/member-level/setting/edit",
        name: MembershipLevelRoute.EditMembershipLevel,
        component: AddOrEditMembershipLevel,
        meta: {
          title: "编辑会员卡等级",
          hidden: true,
          permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.manageLevels,
        },
      },
    ],
  },
]

export const MembershipLevelChildrenRouter: RouteRecordRaw[] = [
  ...membershipLevelRouter,
]

function createMembershipRiskWarningRoutes(): RouteRecordRaw[] {
  if (!MEMBER_RISK_AVAILABLE) return []
  const MemberRiskStoreCustomerList = () => import("@/views/page/member-risk/store/customer-list/index.vue")
  const MemberRiskStoreEliminationRecords = () => import("@/views/page/member-risk/store/elimination-records/index.vue")

  return [
    {
      path: "/membership/risk-warning/customer-list",
      redirect: "/membership/risk-warning/customer-list/index",
      component: markRaw(LevelTwoRouter),
      meta: {
        title: "本店风险客户",
        hidden: true,
        permissions: MEMBERSHIP_RISK_WARNING_GROUP_PERMISSIONS,
        scopeTypes: STORE_SCOPE_TYPES,
        notAccessImage,
      },
      children: [
        {
          path: "/membership/risk-warning/customer-list/index",
          component: MemberRiskStoreCustomerList,
          name: "MembershipRiskWarningCustomerList",
          meta: {
            title: "本店风险客户",
            hidden: true,
            permissions: MEMBERSHIP_RISK_WARNING_GROUP_PERMISSIONS,
            scopeTypes: STORE_SCOPE_TYPES,
            notAccessImage,
          },
        },
      ],
    },
    {
      path: "/membership/risk-warning/elimination-records",
      redirect: "/membership/risk-warning/elimination-records/index",
      component: markRaw(LevelTwoRouter),
      meta: {
        title: "历史消除记录",
        hidden: true,
        permissions: MEMBERSHIP_RISK_WARNING_GROUP_PERMISSIONS,
        scopeTypes: STORE_SCOPE_TYPES,
        notAccessImage,
      },
      children: [
        {
          path: "/membership/risk-warning/elimination-records/index",
          component: MemberRiskStoreEliminationRecords,
          name: "MembershipRiskWarningEliminationRecords",
          meta: {
            title: "历史消除记录",
            hidden: true,
            permissions: MEMBERSHIP_RISK_WARNING_GROUP_PERMISSIONS,
            scopeTypes: STORE_SCOPE_TYPES,
            notAccessImage,
          },
        },
      ],
    },
  ]
}

const MembershipRiskWarningRouter: RouteRecordRaw[] = createMembershipRiskWarningRoutes()

export const MembershipRiskWarningChildrenRouter: RouteRecordRaw[] = [
  ...MembershipRiskWarningRouter,
]

const firstMembershipLevelRoute: RouteRecordRaw[] = [
  {
    path: "/membership/member-manage",
    redirect: "/membership/member-manage/member-stat",
    meta: {
      title: "会员管理",
      permissions: MEMBERSHIP_MEMBER_MANAGE_GROUP_PERMISSIONS,
      notAccessImage,
      activeMenu: '/membership/member-manage',
    },
    children: injectRouteList(MembershipManageChildrenRouter),
  },
  {
    path: "/membership/business",
    redirect: "/membership/business/consumption-record",
    meta: {
      title: "交易记录",
      permissions: MEMBERSHIP_BUSINESS_GROUP_PERMISSIONS,
      notAccessImage,
      activeMenu: '/membership/business',
    },
    children: injectRouteList(MembershipBusinessChildrenRouter),
  },
  {
    path: "/membership/credit",
    redirect: "/membership/credit/credit-shop",
    meta: {
      title: "积分管理",
      permissions: MEMBERSHIP_CREDIT_GROUP_PERMISSIONS,
      notAccessImage,
      activeMenu: '/membership/credit',
    },
    children: injectRouteList(MembershipCreditChildrenRouter),
  },
  {
    path: "/membership/marketing",
    redirect: "/membership/marketing/charge-rule",
    meta: {
      title: "会员营销",
      permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.viewChargeRules,
      notAccessImage,
      activeMenu: '/membership/marketing',
    },
    children: injectRouteList(MemberChargeRuleChildrenRouter),
  },
  {
    path: "/membership/dashboard",
    redirect: "/membership/dashboard/member-consume",
    meta: {
      title: "会员报表",
      permissions: ["dashboard_member"],
      notAccessImage,
      activeMenu: '/membership/dashboard',
    },
    children: injectRouteList(MembershipDashboardRouter),
  },
  {
    path: "/membership/member-level",
    redirect: "/membership/member-level/setting",
    meta: {
      title: "会员设置",
      permissions: MEMBERSHIP_SETTING_ACTION_PERMISSIONS.viewLevels,
      notAccessImage,
      activeMenu: '/membership/member-level',
    },
    children: injectRouteList(MembershipLevelChildrenRouter),
  },
  ...(MEMBER_RISK_AVAILABLE ? [{
    path: "/membership/risk-warning",
    redirect: "/membership/risk-warning/customer-list",
    meta: {
      title: "风险预警",
      permissions: MEMBERSHIP_RISK_WARNING_GROUP_PERMISSIONS,
      notAccessImage,
      activeMenu: "/membership/risk-warning",
      scopeTypes: ["chain-store"],
    },
    children: injectRouteList(MembershipRiskWarningChildrenRouter),
  } satisfies RouteRecordRaw] : []),
]
const memberships: RouteRecordRaw[] = [
  {
    path: "/membership",
    component: Layout,
    redirect: "/membership/member-manage/member-stat",
    children: injectRouteList(firstMembershipLevelRoute),
    meta: {
      title: "会员",
      showTag: false,
      svgIcon: "nav-member",
      order: 2,
      permissions: MEMBERSHIP_ACTION_PERMISSIONS.view,
      notAccessImage,
    },
  },
]
export default memberships
